import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart3 } from "lucide-react";
import { SortableHead, useSortedRows } from "@/components/SortableHeader";

export const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);

const STATUS = {
  paid: "bg-green-100 text-green-700", pending: "bg-orange-100 text-orange-700", sent: "bg-blue-100 text-blue-700",
  partial: "bg-indigo-100 text-indigo-700", overdue: "bg-red-100 text-red-700", open: "bg-orange-100 text-orange-700", closed: "bg-green-100 text-green-700",
};
export const StatusBadge = ({ status }) => <Badge className={STATUS[status] || "bg-slate-100 text-slate-700"}>{String(status || "").replace("_", " ").toUpperCase()}</Badge>;

export const Empty = ({ title = "No records found" }) => (
  <div className="text-center py-12" data-testid="report-empty">
    <BarChart3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
    <h3 className="text-xl font-semibold text-slate-700 mb-2">{title}</h3>
    <p className="text-slate-500">Try adjusting your filters</p>
  </div>
);

export const TotalsBar = ({ cells }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="report-totals-bar">
    {cells.map((c) => (
      <Card key={c.label} className="bg-white border-0 shadow"><CardContent className="p-4"><p className="text-xs uppercase tracking-wide text-slate-500">{c.label}</p><p className={`text-xl font-bold ${c.cls || "text-slate-800"}`} data-testid={c.testId}>{c.value}</p></CardContent></Card>
    ))}
  </div>
);

// Generic sortable table: columns = [{ key, label, accessor, render?, align? }]
export const ReportTable = ({ rows, columns, defaultSort, defaultDir = "desc", rowKey, testId, footer }) => {
  const accessors = Object.fromEntries(columns.map((c) => [c.key, c.accessor]));
  const { sorted, sort, toggle } = useSortedRows(rows, accessors, defaultSort, defaultDir);
  if (rows.length === 0) return <Empty />;
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-lg">
      <Table data-testid={testId}>
        <TableHeader>
          <TableRow className="bg-slate-50">
            {columns.map((c) => <SortableHead key={c.key} label={c.label} sortKey={c.key} sort={sort} onToggle={toggle} className={c.align === "right" ? "text-right" : ""} align={c.align} />)}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((r) => (
            <TableRow key={rowKey(r)} className="hover:bg-slate-50" data-testid="report-row">
              {columns.map((c) => <TableCell key={c.key} className={`${c.align === "right" ? "text-right" : ""} ${c.className || ""}`}>{c.render ? c.render(r) : c.accessor(r)}</TableCell>)}
            </TableRow>
          ))}
          {footer && (
            <TableRow className="bg-indigo-50 font-bold" data-testid="report-footer-row">
              {columns.map((c) => <TableCell key={c.key} className={c.align === "right" ? "text-right" : ""}>{footer[c.key] ?? ""}</TableCell>)}
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

const sum = (rows, key) => rows.reduce((s, r) => s + (r[key] || 0), 0);

export const InvoiceSummaryReport = ({ data, byTechnician }) => {
  const nameKey = byTechnician ? "technician_name" : "customer_name";
  const totalKey = byTechnician ? "total_billed" : "total_invoiced";
  const countKey = byTechnician ? "completed_entries" : "invoice_count";
  const totals = [
    { label: byTechnician ? "Technicians" : "Customers", value: data.length },
    { label: byTechnician ? "Total Billed" : "Total Invoiced", value: money(sum(data, totalKey)), cls: "text-blue-600", testId: "summary-total" },
    byTechnician ? { label: "Total Hours", value: `${sum(data, "total_hours").toFixed(1)} h`, cls: "text-indigo-600" } : { label: "Paid", value: money(sum(data, "total_paid")), cls: "text-green-600" },
    byTechnician ? { label: "Avg $/hour", value: money(sum(data, "total_hours") ? sum(data, totalKey) / sum(data, "total_hours") : 0), cls: "text-slate-700" } : { label: "Outstanding", value: money(sum(data, "total_unpaid")), cls: "text-orange-600" },
  ];
  const columns = byTechnician
    ? [
        { key: "name", label: "Technician", accessor: (r) => r[nameKey], render: (r) => <span className="font-semibold text-slate-800">{r[nameKey]}<p className="text-xs text-slate-500 font-normal">{r.specialty}</p></span> },
        { key: "entries", label: "Completed Entries", accessor: (r) => r[countKey], align: "right" },
        { key: "hours", label: "Hours", accessor: (r) => r.total_hours, render: (r) => `${(r.total_hours || 0).toFixed(1)} h`, align: "right" },
        { key: "billed", label: "Billed", accessor: (r) => r[totalKey], render: (r) => <span className="font-bold text-blue-600">{money(r[totalKey])}</span>, align: "right" },
      ]
    : [
        { key: "name", label: "Customer", accessor: (r) => r[nameKey], render: (r) => <span className="font-semibold text-slate-800">{r[nameKey]}<p className="text-xs text-slate-500 font-normal">{r.customer_email}</p></span> },
        { key: "count", label: "Invoices", accessor: (r) => r[countKey], align: "right" },
        { key: "total", label: "Total", accessor: (r) => r[totalKey], render: (r) => <span className="font-bold text-blue-600">{money(r[totalKey])}</span>, align: "right" },
        { key: "paid", label: "Paid", accessor: (r) => r.total_paid, render: (r) => <span className="text-green-600">{money(r.total_paid)}</span>, align: "right" },
        { key: "pending", label: "Outstanding", accessor: (r) => r.total_unpaid, render: (r) => <span className="text-orange-600">{money(r.total_unpaid)}</span>, align: "right" },
      ];
  const footer = byTechnician
    ? { name: "TOTAL", entries: sum(data, countKey), hours: `${sum(data, "total_hours").toFixed(1)} h`, billed: money(sum(data, totalKey)) }
    : { name: "TOTAL", count: sum(data, countKey), total: money(sum(data, totalKey)), paid: money(sum(data, "total_paid")), pending: money(sum(data, "total_unpaid")) };
  return (
    <div data-testid="invoice-summary-report">
      <TotalsBar cells={totals} />
      <ReportTable rows={data} columns={columns} defaultSort={byTechnician ? "billed" : "total"} rowKey={(r) => r[byTechnician ? "technician_id" : "customer_id"]} testId="summary-table" footer={data.length ? footer : null} />
    </div>
  );
};

export const InvoiceDetailedReport = ({ invoices }) => {
  const t = today();
  const isOverdue = (i) => i.status !== "paid" && i.due_date && i.due_date < t;
  const total = sum(invoices, "total");
  const paid = sum(invoices.filter((i) => i.status === "paid"), "total");
  const totals = [
    { label: "Invoices", value: invoices.length, testId: "detail-count" },
    { label: "Total", value: money(total), cls: "text-blue-600", testId: "detail-total" },
    { label: "Paid", value: money(paid), cls: "text-green-600" },
    { label: "Outstanding", value: money(total - paid), cls: "text-orange-600" },
  ];
  const columns = [
    { key: "invoice_number", label: "Invoice #", accessor: (i) => i.invoice_number, className: "font-semibold text-slate-800" },
    { key: "customer_name", label: "Customer", accessor: (i) => i.customer_name },
    { key: "technician_name", label: "Technician", accessor: (i) => i.technician_name || "", render: (i) => i.technician_name || <span className="text-slate-400">—</span> },
    { key: "created_at", label: "Date", accessor: (i) => String(i.created_at || ""), render: (i) => String(i.created_at || "").slice(0, 10) },
    { key: "due_date", label: "Due", accessor: (i) => i.due_date, render: (i) => <span className={isOverdue(i) ? "text-red-600 font-medium" : ""}>{i.due_date}</span> },
    { key: "status", label: "Status", accessor: (i) => (isOverdue(i) ? "overdue" : i.status), render: (i) => <span className="flex gap-1"><StatusBadge status={i.status} />{isOverdue(i) && <StatusBadge status="overdue" />}</span> },
    { key: "total", label: "Amount", accessor: (i) => i.total, render: (i) => <span className={`font-bold ${i.status === "paid" ? "text-green-600" : "text-blue-600"}`}>{money(i.total)}</span>, align: "right" },
  ];
  return (
    <div data-testid="invoice-detailed-report">
      <TotalsBar cells={totals} />
      <ReportTable rows={invoices} columns={columns} defaultSort="created_at" rowKey={(i) => i.id} testId="detail-table" footer={invoices.length ? { invoice_number: "TOTAL", customer_name: `${invoices.length} invoices`, total: money(total) } : null} />
    </div>
  );
};

export const WorkOrderSummaryReport = ({ data, byTechnician }) => {
  const nameKey = byTechnician ? "technician_name" : "customer_name";
  const doneKey = byTechnician ? "completed_count" : "closed_count";
  const totals = [
    { label: byTechnician ? "Technicians" : "Customers", value: data.length },
    { label: "Work Orders", value: sum(data, "total_workorders"), cls: "text-blue-600", testId: "summary-total" },
    { label: "Total Hours", value: `${sum(data, "total_hours").toFixed(1)} h`, cls: "text-indigo-600" },
    { label: "Open / Closed", value: `${sum(data, "open_count")} / ${sum(data, doneKey)}`, cls: "text-orange-600" },
  ];
  const columns = [
    { key: "name", label: byTechnician ? "Technician" : "Customer", accessor: (r) => r[nameKey], render: (r) => <span className="font-semibold text-slate-800">{r[nameKey]}<p className="text-xs text-slate-500 font-normal">{byTechnician ? r.specialty : (r.technicians || []).join(", ")}</p></span> },
    { key: "orders", label: "Work Orders", accessor: (r) => r.total_workorders, align: "right" },
    { key: "hours", label: "Hours", accessor: (r) => r.total_hours, render: (r) => <span className="font-bold text-blue-600">{(r.total_hours || 0).toFixed(1)} h</span>, align: "right" },
    { key: "open", label: "Open", accessor: (r) => r.open_count, render: (r) => <span className="text-orange-600">{r.open_count}</span>, align: "right" },
    { key: "closed", label: "Closed", accessor: (r) => r[doneKey], render: (r) => <span className="text-green-600">{r[doneKey]}</span>, align: "right" },
  ];
  return (
    <div data-testid="workorder-summary-report">
      <TotalsBar cells={totals} />
      <ReportTable rows={data} columns={columns} defaultSort="hours" rowKey={(r) => r[byTechnician ? "technician_id" : "customer_id"]} testId="summary-table" footer={data.length ? { name: "TOTAL", orders: sum(data, "total_workorders"), hours: `${sum(data, "total_hours").toFixed(1)} h`, open: sum(data, "open_count"), closed: sum(data, doneKey) } : null} />
    </div>
  );
};

export const WorkOrderDetailedReport = ({ entries }) => {
  const hours = sum(entries, "time_spent");
  const totals = [
    { label: "Work Orders", value: entries.length, testId: "detail-count" },
    { label: "Total Hours", value: `${hours.toFixed(1)} h`, cls: "text-blue-600", testId: "detail-total" },
    { label: "Open", value: entries.filter((e) => e.status !== "closed").length, cls: "text-orange-600" },
    { label: "Closed", value: entries.filter((e) => e.status === "closed").length, cls: "text-green-600" },
  ];
  const columns = [
    { key: "date", label: "Date", accessor: (e) => e.date },
    { key: "customer_name", label: "Customer", accessor: (e) => e.customer_name, className: "font-semibold text-slate-800" },
    { key: "technician_name", label: "Technician", accessor: (e) => e.technician_name || "" },
    { key: "description", label: "Description", accessor: (e) => e.description, render: (e) => <p className="max-w-md line-clamp-2" title={e.description}>{e.description}</p> },
    { key: "time_spent", label: "Hours", accessor: (e) => e.time_spent, render: (e) => <span className="font-bold text-blue-600">{(e.time_spent || 0).toFixed(1)} h</span>, align: "right" },
    { key: "status", label: "Status", accessor: (e) => e.status || "open", render: (e) => <StatusBadge status={e.status || "open"} /> },
  ];
  return (
    <div data-testid="workorder-detailed-report">
      <TotalsBar cells={totals} />
      <ReportTable rows={entries} columns={columns} defaultSort="date" rowKey={(e) => e.id} testId="detail-table" footer={entries.length ? { date: "TOTAL", customer_name: `${entries.length} work orders`, time_spent: `${hours.toFixed(1)} h` } : null} />
    </div>
  );
};
